import { useField } from 'formik';
import dynamic from 'next/dynamic';
import { ChangeEventHandler, ComponentProps, FC, useCallback } from 'react';

import { Chevron } from 'src/general/components/Chevron/Chevron';
import { ConditionalWrapper } from 'src/general/components/ConditionalWrapper/ConditionalWrapper';
import { Tooltip, TooltipTrigger } from 'src/general/components/Tooltip/Tooltip';
import { FieldWrapperOptionsGroup } from 'src/general/components/forms/Input/atoms/FieldWrapper';
import { OptionField } from 'src/general/components/forms/Input/atoms/OptionField';
import { toggleValueInArray } from 'src/general/components/forms/Input/atoms/OptionFieldsGroup';
import { pluralise } from 'src/utils/pluralise';

import styles from './PlpFilters.module.scss';
import { FilterOptionHierarchy } from './PlpFilters.type';

export type PlpFiltersHierarchyOnClick = (filter: {
  option: FilterOptionHierarchy;
  fieldName: string;
  filterTitle: string;
}) => void;

export type PlpFiltersHierarchyConditionalProps =
  | {
      type: 'default';
    }
  | {
      type: 'modal';
      onHierarchyClick: PlpFiltersHierarchyOnClick;
    };

type SharedProps = {
  id: string;
  name: string;
  subCategoryLabel: string;
} & PlpFiltersHierarchyConditionalProps;

type Props = Prettify<
  {
    options: FilterOptionHierarchy[];
  } & SharedProps
>;

type ParentOptionProps = Prettify<
  {
    option: FilterOptionHierarchy;
    value: string[];
    'data-testid'?: string;
  } & SharedProps
>;

type ChildrenOptionsProp = {
  id: string;
  name: string;
  option: FilterOptionHierarchy;
  'data-testid'?: string;
};

const fieldType = 'checkbox';

const TooltipContent = dynamic(
  () => import('src/general/components/Tooltip/Tooltip').then((mod) => mod.TooltipContent),
  {
    ssr: false,
  },
);

export const PlpFiltersHierarchy: FC<Props> = ({ id, name, options, ...props }) => {
  const [field, _meta, _helpers] = useField<string[]>(name);
  const value = field.value ?? [];

  /**
   * Adapted from InputCheckboxFormik
   */
  return (
    <FieldWrapperOptionsGroup id={id} name={name} options={options} value={value} type={fieldType}>
      {options.map((option) => {
        const optionId = `${id}-${name}-${option.value}`;
        return (
          <ParentOption
            option={option}
            name={name}
            id={optionId}
            key={optionId}
            data-testid={optionId}
            value={value}
            {...props}
          />
        );
      })}
    </FieldWrapperOptionsGroup>
  );
};

const ParentOption: FC<ParentOptionProps> = ({ option, value, subCategoryLabel, ...props }) => {
  const hasChildren = option.children.length > 0;
  const isDefaultType = props.type === 'default';
  const hasTooltip = hasChildren && isDefaultType;
  const [_field, _meta, helpers] = useField<string[]>(props.name);
  const parentOptionSelected = value.findIndex((singleValue) => singleValue === option.value) >= 0;
  const selectedSubCategoryOptions = value.filter((singleValue) => singleValue.startsWith(`${option.value}>`));
  const indeterminate = selectedSubCategoryOptions.length > 0;

  let optionLabelCount: string | null = null;
  if (indeterminate) {
    optionLabelCount = `${pluralise(subCategoryLabel, selectedSubCategoryOptions.length).toLowerCase()} selected`;
  } else if (parentOptionSelected && hasChildren) {
    optionLabelCount = `All ${pluralise(subCategoryLabel, 2, { skipNumber: true }).toLowerCase()} selected`;
  }

  const changeHandler: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      const eventValue = event.target.value;
      const newFieldValue = toggleFilterHierarchyValue(eventValue, [...value]);
      helpers.setValue(newFieldValue);
    },
    [value, helpers],
  );

  return (
    <ConditionalWrapper condition={hasTooltip} wrapper={tooltipWrapper}>
      <div className={styles.parentOption}>
        <ConditionalWrapper condition={hasTooltip} wrapper={tooltipTriggerWrapper}>
          <OptionField
            {...option}
            {...props}
            value={value}
            type={fieldType}
            option={{
              ...option,
              indeterminate,
              label: optionLabelCount ? (
                <>
                  {option.label}
                  <small>{optionLabelCount}</small>
                </>
              ) : (
                option.label
              ),
            }}
            onChange={changeHandler}
          />
        </ConditionalWrapper>
        {hasChildren && (
          <>
            <Chevron />
            {props.type === 'modal' && (
              <button
                onClick={() =>
                  props.onHierarchyClick({
                    option,
                    fieldName: props.name,
                    filterTitle:
                      props.name === 'makeModel'
                        ? `${option.label}`
                        : `${option.labelNoCount} ${pluralise(subCategoryLabel, 2, { skipNumber: true })}`,
                  })
                }
                className={styles.modalHierarchyTrigger}
              >
                <span className="sr-only">
                  Show {option.labelNoCount} {pluralise(subCategoryLabel, 2, { skipNumber: true })}
                </span>
              </button>
            )}
          </>
        )}
        {hasTooltip && (
          <TooltipContent className={styles.tooltipContent} data-testid={`${props.type}-tooltip-${props.name}`}>
            <>
              <h3 className={styles.tooltipTitle}>
                {option.labelNoCount} {pluralise(subCategoryLabel, 2, { skipNumber: true })}
              </h3>
              <PlpFiltersHierarchyChildrenOptions {...props} option={option} />
            </>
          </TooltipContent>
        )}
      </div>
    </ConditionalWrapper>
  );
};

export const PlpFiltersHierarchyChildrenOptions: FC<ChildrenOptionsProp> = ({ name, option, ...props }) => {
  const [field, _meta, helpers] = useField<string[]>(name);
  const value = field.value ?? [];

  const changeHandler: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      const eventValue = event.target.value;
      const newFieldValue = toggleFilterHierarchyValue(eventValue, [...value]);
      helpers.setValue(newFieldValue);
    },
    [value, helpers],
  );

  return (
    <FieldWrapperOptionsGroup {...props} name={name} value={value} options={option.children} type={fieldType}>
      {option.children.map((childOption) => (
        <OptionField
          {...props}
          name={name}
          value={value}
          type={fieldType}
          option={childOption}
          onChange={changeHandler}
          key={childOption.value}
          data-testid={`${props['data-testid'] ?? 'PlpFilterHierarchy'}-child`}
        />
      ))}
    </FieldWrapperOptionsGroup>
  );
};

export const toggleFilterHierarchyValue = (value: string, fieldValue: string[] = []) => {
  let newFieldValue = toggleValueInArray(value, fieldValue);

  // Special case when toggling on a value
  if (newFieldValue.findIndex((singleValue) => singleValue === value) >= 0) {
    if (value.indexOf('>') >= 0) {
      // Handle the case of a sub-category
      // by removing the "all" category
      const splitCategory = value.split('>');
      newFieldValue = newFieldValue.filter((singleValue) => singleValue !== splitCategory[0]);
    } else {
      // Handles the case where "all" is selected
      // by removing any of the sub categories
      newFieldValue = newFieldValue.filter((singleValue) => !singleValue.startsWith(`${value}>`));
    }
  }
  return newFieldValue;
};

const tooltipWrapper: ComponentProps<typeof ConditionalWrapper>['wrapper'] = (children) => (
  <Tooltip placement="right-start" fallbackPlacements={['right-start', 'right', 'right-end']}>
    {children}
  </Tooltip>
);

const tooltipTriggerWrapper: ComponentProps<typeof ConditionalWrapper>['wrapper'] = (children) => (
  <TooltipTrigger>{children}</TooltipTrigger>
);
